import { ReactComponent as CheckmarkSVG } from "@svg/checkmark.svg";
import { ReactComponent as CrossSVG } from "@svg/cross.svg";
import { ReactComponent as SelectArrowsSVG } from "@svg/select_arrows.svg";
import { t } from "i18next";
import { LegacyRef, useMemo } from "react";
import Select, {
  ActionMeta,
  GroupBase,
  MenuPlacement,
  OnChangeValue,
  SingleValueProps,
  StylesConfig,
  components as reactSelectComponents,
} from "react-select";
import { GenericComponentProps } from "../../../utils/genericComponentProps";
import { Color, statusBackgroundColorMapping, statusBorderColorMapping } from "../../ui-components/Selectors/ColorSelect/ColorSelect.utils";
import { Popup } from "../Popup/Popup";
import { StatusTagColor } from "../StatusTag/StatusTag";
import { Text } from "../Text/Text";
const { Option, MultiValueRemove, ClearIndicator, DropdownIndicator, SingleValue, Menu, ValueContainer, Control } = reactSelectComponents;

export type MenuPortalTargetOptions = HTMLElement | "popup" | "insideModal";

/**
 * @deprecated use SelectInput from ui-components instead
 */
export class SelectInputData {
  value: any;

  label: string;

  disabled?: boolean;

  fixed?: boolean;

  constructor(value: any, label: string, disabled = false, fixed = false) {
    this.value = value;
    this.label = label;

    this.disabled = disabled;
    this.fixed = fixed;
  }
}

/**
 * @deprecated use SelectInput from ui-components instead
 */
export type SelectInputFieldProps<IsMulti, TOptions extends SelectInputData[]> = {
  options: TOptions;
  value?: (IsMulti extends true ? TOptions[number]["value"][] : TOptions[number]["value"]) | null;
  onChange: (newValue: OnChangeValue<SelectInputData, IsMulti extends true ? true : false>, actionMeta: ActionMeta<SelectInputData>) => unknown;
  selectedOptions?: TOptions;
  /**
   * @deprecated please use `value` instead
   **/
  defaultValue?: TOptions;
  multi?: IsMulti;
  disabled?: boolean;
  inputRef?: LegacyRef<any>;
  ref?: null;

  label?: string;
  helperText?: string;
  helperTextColor?: string;
  placeholder?: string;

  creatable?: boolean;

  allowCreateWhileLoading?: boolean;
  loading?: boolean;

  backgroundColor?: string;

  singleValueRenderer?: (props: SingleValueProps<SelectInputData>) => any;
  useCustomSort?: boolean;
  isSearchable?: boolean;
  isClearable?: boolean;
  menuPortalTarget?: MenuPortalTargetOptions;
  defaultMenuIsOpen?: boolean;
  menuPlacement?: MenuPlacement;

  dataCy?: string;
  customHeader?: JSX.Element;

  hasSelectedCheckmark?: boolean;
  isCapitalizedOptions?: boolean;

  // related to color select
  hasColorSquare?: boolean;
} & Omit<GenericComponentProps, "style">;

const CustomValueContainer = (componentProps: any) => {
  return (
    <ValueContainer
      {...componentProps}
      innerProps={{
        ...componentProps?.innerProps,
        "data-cy": "value",
      }}
    />
  );
};
const CustomControl = ({ children, ...props }: any) => (
  <Control {...props} innerProps={{ ...props?.innerProps, "data-cy": props.dataCy && `${props.dataCy}-control` }}>
    {children}
  </Control>
);
interface WithDataCyProps {
  dataCy: string;
}
const withDataCy = <Props extends object>(WrappedComponent: React.ComponentType<Props & WithDataCyProps>) => {
  return (props: Props & WithDataCyProps) => {
    const { dataCy, ...rest } = props;
    return <WrappedComponent {...(rest as Props)} dataCy={dataCy} />;
  };
};
// Higher order component to add data-cy to the control component
const CustomControlWithDataCy = withDataCy(CustomControl);

const renderColorSquare = (color: StatusTagColor = "grey") => ({
  backgroundColor: statusBackgroundColorMapping[color as keyof Color],
  borderWidth: 1,
  borderColor: statusBorderColorMapping[color as keyof Color],
  borderRadius: 3,
  borderStyle: "solid",
  content: '" "',
  display: "block",
  marginRight: 8,
  height: 18,
  width: 18,
});

/**
 * @deprecated use SelectInput from ui-components instead
 */
export const SelectInputField = <IsMulti, TOptions extends SelectInputData[]>({
  creatable,
  disabled,
  label,
  helperText,
  multi,
  placeholder,
  allowCreateWhileLoading,
  loading,
  onChange: onSelect,
  isCapitalizedOptions,
  hasColorSquare,
  hasSelectedCheckmark = true,
  ...props
}: SelectInputFieldProps<IsMulti, TOptions>) => {
  const selectStyling: StylesConfig<SelectInputData, IsMulti extends true ? true : false, GroupBase<SelectInputData>> = {
    control: (base, state) => ({
      ...base,
      cursor: "pointer",
      width: "100%",
      minWidth: props.width ?? "200px",
      borderRadius: "12px",
      border: "1px solid var(--border-color)",
      outline: 0,
      backgroundColor: props.backgroundColor ?? "#fff",
      padding: "0px 4px",
      fontWeight: 400,
      boxSizing: "border-box",
      fontSize: "16px",
      marginTop: props.marginTop,
      marginBottom: props.marginBottom,
      marginLeft: props.marginLeft,
      marginRight: props.marginRight,
      textTransform: isCapitalizedOptions ? "capitalize" : "initial",

      ...(state.isDisabled
        ? {
            borderColor: "#A19EA8",
            backgroundColor: "#F5F2F5",
          }
        : {}),
      ...(state.isFocused
        ? {
            borderColor: "#73717A !important",
            boxShadow: "none !important",
          }
        : {}),
    }),

    input: (styles) => ({
      ...styles,

      display: "flex",
      alignItems: "center",
      lineHeight: "20px",

      ":before": {
        ...(hasColorSquare && { ...renderColorSquare() }),
        boxSizing: "border-box",
      },
    }),

    singleValue: (styles, { data }) => ({
      ...styles,

      display: "flex",
      alignItems: "center",
      lineHeight: "20px",

      ":before": {
        ...(hasColorSquare && { ...renderColorSquare(data.value as StatusTagColor) }),
        boxSizing: "border-box",
      },
    }),

    option: (base, state) => ({
      ...base,

      textTransform: isCapitalizedOptions ? "capitalize" : "initial",
      cursor: "pointer",
      margin: "4px 0",

      "&:active": {
        backgroundColor: "#F2EEFD",
      },

      display: "flex",
      alignItems: "center",
      lineHeight: "20px",

      ":before": {
        ...(hasColorSquare && { ...renderColorSquare(state.data.value) }),
        boxSizing: "border-box",
      },

      ...(state.isSelected || state.isFocused || state.isSelected
        ? {
            color: "var(--primary-text-color)",
            backgroundColor: "#F2EEFD",
            borderRadius: "8px",
          }
        : {}),
    }),
    valueContainer: (base) => ({ ...base, ...(props.height ? { height: props.height } : {}) }),
    dropdownIndicator: () => ({ display: "flex", paddingleft: "10px", marginRight: "6px" }),
    menu: (base) => ({
      ...base,

      zIndex: 999,
      padding: "0px 6px",
      border: "1px solid #D4D4D9",
      borderRadius: "8px",
      boxShadow: "0px 10px 20px rgba(74, 61, 92, 0.15), 0px 3px 6px rgba(74, 61, 92, 0.1)",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    menuPortal: (base) => ({
      ...base,

      zIndex: props.menuPortalTarget ? 9999 : undefined,
    }),
    multiValue: (base, state) => ({
      ...base,

      padding: "2px 4px",
      color: "var(--primary-text-color)",
      backgroundColor: state.data.fixed ? "var(--shadow-color)" : "#F2EEFD",
      borderRadius: "8px",
    }),
    clearIndicator: (base) => ({
      ...base,

      color: "var(--primary-text-color)",

      "&:hover": {
        color: "var(--primary-text-color)",
      },
    }),
    multiValueRemove: (base, state) => {
      if (state.data.fixed) {
        return { ...base, display: "none" };
      }

      return {
        ...base,

        "&:hover": {
          backgroundColor: "transparent",
          color: "var(--main-red-700)",
        },
      };
    },
  };

  const sortedOptions = useMemo(
    () => (props.useCustomSort ? props.options : props.options.sort((a, b) => a.label.localeCompare(b.label))),
    [props.options, props.useCustomSort]
  );

  const optionsSelected = useMemo(() => {
    // react-select treats `undefined` differently from `null`
    // `null` means "no option(s) selected", while `undefined` means "this field is uncontrolled".

    if (props.value === undefined && props.selectedOptions === undefined) return undefined;

    if (props.selectedOptions) return props.selectedOptions;

    if (props.value === null || props.value?.length === 0) return null;

    if (Array.isArray(props.value)) {
      return props.value
        .map((selectedValue: any) =>
          props.options?.find((option) => {
            if (typeof selectedValue === "object") {
              return option.value === selectedValue.value;
            }

            return option.value === selectedValue;
          })
        )
        .filter((value: any) => value !== undefined) as SelectInputData[];
    } else {
      const selectedOption = props.options?.find((option) => option.value === props.value);

      return selectedOption ? [selectedOption] : null;
    }
  }, [props.options, props.selectedOptions, props.value]);

  const getMenuPortalTarget = () => {
    if (!props.menuPortalTarget) return undefined;
    if (props.menuPortalTarget === "popup") return document.getElementById(Popup.popup_div_id) as HTMLElement;
    if (props.menuPortalTarget === "insideModal") return document.body;
    return props.menuPortalTarget;
  };

  const onChange = (newValue: OnChangeValue<SelectInputData, IsMulti extends true ? true : false>, actionMeta: ActionMeta<SelectInputData>) => {
    if (multi) {
      switch (actionMeta.action) {
        case "remove-value":
        case "pop-value":
          if (actionMeta?.removedValue?.fixed) {
            return;
          }
          break;
        case "clear":
          (newValue as SelectInputData[]) = sortedOptions.filter((v) => v.fixed);
          break;
      }
    }

    onSelect(newValue, actionMeta);
  };

  return (
    <div data-cy={props.dataCy} className={`select ${props.className ?? ""}`} style={{ width: props.width }}>
      {label && (
        <Text size="sm" marginBottom={2}>
          {label}
        </Text>
      )}

      <Select
        ref={props.inputRef}
        isDisabled={disabled}
        isLoading={loading}
        hideSelectedOptions={false}
        styles={selectStyling}
        placeholder={placeholder ?? t("main.select")}
        closeMenuOnSelect={!multi}
        classNamePrefix={"react-select"}
        defaultValue={props.defaultValue}
        isMulti={multi as (IsMulti extends true ? true : false) | undefined}
        value={optionsSelected}
        options={sortedOptions}
        menuPortalTarget={getMenuPortalTarget()}
        onChange={onChange}
        defaultMenuIsOpen={props.defaultMenuIsOpen}
        isClearable={props.isClearable}
        isSearchable={props.isSearchable ?? true}
        menuPlacement={props.menuPlacement}
        isOptionDisabled={(option) => (option.disabled || option.fixed) ?? false}
        components={{
          Control: CustomControlWithDataCy,
          ValueContainer: CustomValueContainer,
          MultiValueRemove: (componentProps: any) => (
            <MultiValueRemove
              {...componentProps}
              innerProps={{ ...componentProps.innerProps, "data-cy": "selectedItem-remove-icon" }}
            ></MultiValueRemove>
          ),
          SingleValue: (componentProps: any) => (
            <SingleValue {...componentProps}>
              {props.singleValueRenderer ? props.singleValueRenderer(componentProps) : componentProps.data.label}
            </SingleValue>
          ),
          Option: (componentProps: any) => (
            <Option
              {...componentProps}
              innerProps={{
                ...componentProps.innerProps,
                "data-cy": props.dataCy && `${props.dataCy}-option-${componentProps.label}`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center" }}>
                {hasSelectedCheckmark && componentProps.isSelected && <CheckmarkSVG style={{ marginRight: "6px", flexShrink: "0" }} />}
                {componentProps.data.label}
              </div>
            </Option>
          ),
          ClearIndicator: (componentProps: any) => (
            <ClearIndicator {...componentProps} innerProps={{ ...componentProps.innerProps, "data-cy": "clear-all-button" }}>
              <CrossSVG scale="24px" color="var(--text-2-color)" spacing={5} />
            </ClearIndicator>
          ),
          DropdownIndicator: (componentProps: any) => (
            <DropdownIndicator {...componentProps} innerProps={{ ...componentProps.innerProps, "data-cy": "select-dropdown-indicator" }}>
              <SelectArrowsSVG />
            </DropdownIndicator>
          ),
          Menu: (componentProps) => (
            <Menu {...(componentProps as any)}>
              {props.customHeader}
              {componentProps.children}
            </Menu>
          ),
        }}
      />

      {helperText && (
        <Text size="sm" marginTop={4} customColor={props.helperTextColor}>
          {helperText}
        </Text>
      )}
    </div>
  );
};
